<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router';
import Layout from '../cross-app-frontend/Layout.vue';
import ModalStack from '../cross-app-frontend/modals/ModalStack.vue';
import { Toaster } from '../cross-app-frontend/toast';
import { useTriNavMenu } from '@/services/navigation/navigation-tri';
import { useEmissionsNavMenu } from '@/services/navigation/navigation-emissions';
import { useTierIINavMenu } from '@/tierii/services/navigation';
import { useHazWasteNavMenu } from '@/hazwaste/services/navigation';
import { computed, ref, useTemplateRef } from 'vue';
import { face } from '@/services/navigation/navigation';
import {
  facilityFromRouteOrLastVisited,
  yearFromRouteOrLastVisited,
  yearUpdateInRoute,
  breadcrumbs,
  facilityUpdateInRoute,
} from '@/router';
import AIChatSheet from '@/components/ai/AIChatSheet.vue';
import ProgressTracker from '@/components/progress/ProgressTracker.vue';
import TierIIProgressTracker from '@/tierii/components/TierIIProgressTracker.vue';
import { slugFromName } from '../tri-shared/services/formatting';
import { SidebarMenuButton, SidebarMenuItem } from '../cross-app-frontend/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../cross-app-frontend/dropdown-menu';
import { Badge } from '../cross-app-frontend/badge';
import calendarImg from '@/assets/calendar.png';

const route = useRoute();
const router = useRouter();
const triNavMenu = useTriNavMenu(router.currentRoute);
const emissionsMenu = useEmissionsNavMenu(router.currentRoute);
const tierIIMenu = useTierIINavMenu(router.currentRoute);
const hazWasteMenu = useHazWasteNavMenu(router.currentRoute);

// The sidebar's Mission Control link, to this app's copy of the platform landing page.
const platformTo = computed(() => {
  const facility = facilityFromRouteOrLastVisited.value;
  return {
    name: 'Platform' as const,
    params: facility ? { tenantID: facility.tenantID, facilityID: facility.facilityID } : {},
  };
});

const navMenu = computed(() => {
  if (face.value === 'emissions') return emissionsMenu.value;
  if (face.value === 'tierii') return tierIIMenu.value;
  if (face.value === 'hazwaste') return hazWasteMenu.value;
  return triNavMenu.value;
});
const showYearPicker = computed(() => face.value === 'tri' || face.value === 'tierii');
const showTriProgressTracker = computed(() => face.value === 'tri');
const showTierIIProgressTracker = computed(() => face.value === 'tierii');
const aiAvailable = computed(() => face.value === 'tri');

// AI Chat and Help functions
const aiChatSheet = useTemplateRef('aiChatSheet');

function openAIChat() {
  if (aiChatSheet.value) {
    aiChatSheet.value.open();
  }
}

const years = ref<number[]>([]);
const currentYear = new Date().getUTCFullYear();
const currentDate = new Date();
const isAfterJuly1 = currentDate.getMonth() >= 6; // July is month 6 (0-indexed)

// Before July 1st: allow years from 2023 to (current year - 1)
// After July 1st: allow years from 2023 to current year
const maxYear = isAfterJuly1 ? currentYear : currentYear - 1;

for (let i = 2023; i <= maxYear; i++) {
  years.value.push(i);
}
</script>

<template>
  <div>
    <template v-if="route.name">
      <RouterView v-if="route.name === 'Login' || route.name === 'Raffle' || route.name === 'Platform'" />
      <Layout
        v-else
        :navMenu
        :breadcrumbs
        :facilityFromRouteOrLastVisited
        :includeWelcome="true"
        padding
        :compactHeader="false"
        :platformTo
        @facilityUpdate="facilityUpdateInRoute($event)"
      >
        <template #afterFacilityPicker>
          <SidebarMenuItem v-if="showYearPicker">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  <div
                    class="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg"
                  >
                    <img :src="calendarImg" alt="Calendar" class="h-7 w-7 object-contain" />
                  </div>
                  <span>{{ yearFromRouteOrLastVisited }}</span>
                  <i class="fa-light fa-angles-up-down ml-auto"></i>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent class="w-48 rounded-lg" side="bottom" align="end">
                <DropdownMenuItem
                  v-for="year of years"
                  :key="year"
                  @click="yearUpdateInRoute(year)"
                  class="cursor-pointer"
                >
                  <i
                    class="fa-light fa-fw"
                    :class="{
                      'fa-calendar-circle': year === yearFromRouteOrLastVisited,
                      'fa-calendar-arrow-up': year < yearFromRouteOrLastVisited,
                      'fa-calendar-arrow-down': year > yearFromRouteOrLastVisited,
                    }"
                  ></i>
                  <span> {{ year }} <Badge v-if="year === yearFromRouteOrLastVisited">Current</Badge> </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </template>
        <template #afterBreadcrumbs>
          <!-- Progress Tracker and AI Chat in top right -->
          <div v-if="facilityFromRouteOrLastVisited?.facilityID" class="relative ml-auto flex items-center gap-2 pr-4">
            <!-- Ask Button - Direct AI Chat -->
            <div class="relative z-20" style="width: 48px; height: 48px">
              <button
                v-if="aiAvailable"
                @click="openAIChat()"
                class="group absolute inset-0 h-12 w-12 cursor-pointer outline-hidden"
                type="button"
                title="Chat with AI assistant"
              >
                <!-- Yellow Border SVG -->
                <svg class="absolute inset-0 h-12 w-12" viewBox="0 0 48 48">
                  <!-- Yellow circle border -->
                  <circle
                    cx="24"
                    cy="24"
                    r="20"
                    stroke="#f4c12f"
                    stroke-width="3"
                    fill="none"
                    class="transition-all duration-200 group-hover:stroke-4"
                  />
                </svg>

                <!-- Inner content button -->
                <div
                  class="absolute inset-1 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white p-0 opacity-80 shadow-xs transition-all duration-200 group-hover:bg-yellow-50 group-hover:shadow-md"
                >
                  <!-- Ask text -->
                  <span class="gradient-underline text-xs font-bold text-gray-700">Ask</span>
                </div>
              </button>
            </div>

            <!-- Hidden AI Chat Component -->
            <AIChatSheet
              v-if="aiAvailable && facilityFromRouteOrLastVisited"
              ref="aiChatSheet"
              :facilityID="facilityFromRouteOrLastVisited.facilityID"
              :year="yearFromRouteOrLastVisited"
              triggerVariant="ghost"
              triggerSize="sm"
              triggerText=""
              triggerIcon=""
              title="TRI Assistant"
              systemPrompt="You are a helpful AI assistant for a TRI (Toxic Release Inventory) reporting application. You can help users with questions about chemical reporting, product management, facility compliance, and environmental regulations."
              placeholder="Ask about TRI reporting, chemicals, or compliance..."
            >
              <template #trigger>
                <div style="display: none"></div>
              </template>
            </AIChatSheet>

            <!-- Progress Tracker -->
            <ProgressTracker
              v-if="showTriProgressTracker && facilityFromRouteOrLastVisited"
              :facilityID="facilityFromRouteOrLastVisited.facilityID"
              :year="yearFromRouteOrLastVisited"
              class="relative z-10 opacity-80"
            />

            <!-- Tier II Progress Tracker -->
            <TierIIProgressTracker
              v-if="showTierIIProgressTracker && facilityFromRouteOrLastVisited"
              :facilityID="facilityFromRouteOrLastVisited.facilityID"
              :facilitySlug="slugFromName(facilityFromRouteOrLastVisited.name)"
              :year="yearFromRouteOrLastVisited"
              class="relative z-10 opacity-80"
            />
          </div>
        </template>
      </Layout>
    </template>
    <Toaster />
    <ModalStack />
  </div>
</template>
